import React from 'react';
import { FileText, Shield, Users, CreditCard, XCircle, AlertTriangle, Ticket } from 'lucide-react';
import { COMPANY_CONFIG } from '../config/config';

const TermsAndConditions = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const sections = [
    {
      icon: FileText,
      title: 'Introduction',
      subsections: [
        {
          number: '1.1',
          content: `These Terms govern your use of the ${COMPANY_CONFIG.appname} platform and all related services.`
        },
        {
          number: '1.2',
          content: 'By using our platform, you confirm that you have read, understood, and agreed to these Terms.'
        },
        {
          number: '1.3',
          content: 'If you do not agree with any part of these Terms, please discontinue use immediately.'
        }
      ]
    },
    {
      icon: Users,
      title: 'Our Role',
      subsections: [
        {
          number: '2.1',
          content: `${COMPANY_CONFIG.appname} acts as a service-connecting platform, helping users connect with service providers.`
        },
        {
          number: '2.2',
          content: 'Visual Soft does not control, manage, or guarantee the services delivered by third-party providers.'
        },
        {
          number: '2.3',
          content: 'We are not responsible for any service provider\'s performance, delays, quality, pricing, or behavior.'
        }
      ]
    },
    {
      icon: Users,
      title: 'User Responsibilities',
      subsections: [
        {
          number: '3.1',
          content: 'Users must provide accurate and up-to-date information.'
        },
        {
          number: '3.2',
          content: 'Users agree not to misuse the platform or violate any laws during use.'
        },
        {
          number: '3.3',
          content: 'All transactions and agreements between users and service providers are made at their own risk.'
        },
        {
          number: '3.4',
          content: `${COMPANY_CONFIG.appname}/Visual Soft is not involved in resolving disputes between users and service providers.`
        }
      ]
    },
    {
      icon: Shield,
      title: 'Service Provider Responsibilities',
      subsections: [
        {
          number: '4.1',
          content: 'Service providers must ensure that their service details, pricing, and availability are accurate.'
        },
        {
          number: '4.2',
          content: 'They are fully responsible for delivering services to users as agreed.'
        },
        {
          number: '4.3',
          content: 'Providers must hold the necessary qualifications, approvals, and licenses related to the services they offer.'
        }
      ]
    },
    {
      icon: CreditCard,
      title: 'Booking & Payments',
      subsections: [
        {
          number: '5.1',
          content: 'All bookings are handled according to each service provider\'s terms.'
        },
        {
          number: '5.2',
          content: 'Payments may be processed through a third-party payment gateway.'
        },
        {
          number: '5.3',
          content: 'Users must verify payment details and ensure available funds.'
        },
        {
          number: '5.4',
          content: `A platform service fee may be charged by ${COMPANY_CONFIG.appname} and is non-refundable, and will be shown before confirmation.`
        }
      ]
    },
    {
      icon: XCircle,
      title: 'Cancellations & Refunds',
      subsections: [
        {
          number: '6.1',
          content: 'Each service provider has their own cancellation and refund policy.'
        },
        {
          number: '6.2',
          content: `${COMPANY_CONFIG.appname} may charge an additional cancellation fee where applicable.`
        },
        {
          number: '6.3',
          content: 'Refunds (if eligible) will be processed subject to the provider\'s policy and after deducting non-refundable platform fees.'
        }
      ]
    },
    {
      icon: FileText,
      title: 'Intellectual Property Rights',
      subsections: [
        {
          number: '7.1',
          content: 'All website content, branding, graphics, and materials belong to Visual Soft.'
        },
        {
          number: '7.2',
          content: 'No content may be copied, reproduced, or used without written permission.'
        }
      ]
    },
    {
      icon: AlertTriangle,
      title: 'Limitation of Liability',
      content: 'To the maximum extent permitted by law, Visual Soft is not liable for:',
      list: [
        'Issues arising from the use or inability to use the platform',
        'Actions or failures of service providers',
        'Booking errors, cancellations, delays, or changes',
        'Loss of data, income, or profits',
        'Unauthorized access to user information',
        'Service interruptions or technical issues'
      ],
      note: 'Our total liability, if any, will be limited to the platform service fee paid for the related booking.'
    },
    {
      icon: Shield,
      title: 'Indemnification',
      content: `You agree to protect and hold harmless Visual Soft and ${COMPANY_CONFIG.appname} from any claims, damages, losses, or expenses arising from:`,
      list: [
        'Your use of the platform',
        'Your violation of these Terms',
        'Your interactions or disputes with service providers'
      ]
    },
    {
      icon: FileText,
      title: 'Changes to Terms',
      subsections: [
        {
          number: '10.1',
          content: 'Visual Soft may update or modify these Terms at any time.'
        },
        {
          number: '10.2',
          content: 'Continued use of the platform following updates means you accept the new Terms.'
        }
      ]
    },
    {
      icon: Users,
      title: 'Contact Information',
      content: 'For assistance or inquiries:',
      details: [
        COMPANY_CONFIG.appname,
        'Operated by Visual Soft',
        '📞 '+COMPANY_CONFIG.phone
      ]
    },
    {
      icon: Ticket,
      title: 'Ticket Policies',
      subsections: [
        {
          number: '1.1',
          content: `To access booking services, users must log in to the ${COMPANY_CONFIG.appname} website or mobile application using valid user credentials.`
        },
        {
          number: '1.2',
          content: 'Booking requests should be made at least 3 hours before the intended departure time.'
        },
        {
          number: '1.3',
          content: `Information related to available vehicles, travel dates, departure and arrival times, seats, boarding points, and ticket prices will be displayed on the ${COMPANY_CONFIG.appname} platform.`
        },
        {
          number: '1.4',
          content: 'After reviewing the available options, users may select their preferred vehicle, seat, travel date and time, start point, and destination.'
        },
        {
          number: '1.5',
          content: `Full payment must be completed at the time of booking. The total amount includes ticket fare, applicable taxes, and any platform, service, or convenience fees charged by ${COMPANY_CONFIG.appname}.`
        },
        {
          number: '1.6',
          content: `Once the booking is confirmed, an e-ticket will be emailed to the customer. All e-tickets issued by ${COMPANY_CONFIG.appname} are non-transferable.`
        },
        {
          number: '1.7',
          content: 'The departure and arrival times shown on the ticket are approximate and subject to change based on operational conditions.'
        },
        {
          number: '1.8',
          content: `By making a reservation through the ${COMPANY_CONFIG.appname} platform, you enter into a direct contractual agreement with the respective third-party transport service provider.`
        },
        {
          number: '1.9',
          content: 'Passengers are strongly advised to contact the transport provider directly for confirmation of boarding points or any trip-related inquiries.'
        },
        {
          number: '1.10',
          content: 'At the time of boarding, passengers must present: (a) A printed or digital copy of the e-ticket; and (b) A valid identity document (NIC, Driving License, Passport, Company ID, Student ID, etc.).'
        },
        {
          number: '1.11',
          content: 'Failure to provide a valid ticket and ID may result in denial of boarding.'
        }
      ]
    },
    {
      icon: FileText,
      title: 'Severability',
      content: 'If any part of these Terms is found invalid, the remaining sections will still apply.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">Terms & Conditions</h1>
            <p className="text-xs sm:text-sm text-blue-100">{COMPANY_CONFIG.appname}</p>
            <p className="text-xs text-blue-200 mt-1">Last Updated: {currentDate}</p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed text-lg">
              Welcome to {COMPANY_CONFIG.appname}, operated by Visual Soft. By accessing or using our website, 
              products, or services, you agree to the following Terms & Conditions. Please read them carefully.
            </p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, sectionIndex) => {
              const Icon = section.icon;
              return (
                <div key={sectionIndex} className="bg-white rounded-xl shadow-md p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        {sectionIndex + 1}. {section.title}
                      </h2>
                      
                      {/* Subsections */}
                      {section.subsections && (
                        <div className="space-y-3">
                          {section.subsections.map((subsection, subIndex) => (
                            <div key={subIndex} className="flex items-start space-x-3">
                              <span className="text-blue-600 font-semibold flex-shrink-0">
                                {subsection.number}
                              </span>
                              <p className="text-gray-700 leading-relaxed">
                                {subsection.content}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Content with list or simple content */}
                      {section.content && !section.subsections && (
                        <div className="space-y-3">
                          <p className="text-gray-700 leading-relaxed">
                            {section.content}
                          </p>
                          {section.list && (
                            <ul className="list-disc list-inside space-y-2 ml-4 text-gray-700">
                              {section.list.map((item, itemIndex) => (
                                <li key={itemIndex} className="leading-relaxed">
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                          {section.note && (
                            <p className="text-gray-700 leading-relaxed mt-3 italic">
                              {section.note}
                            </p>
                          )}
                          {section.details && (
                            <div className="space-y-1 mt-3">
                              {section.details.map((detail, detailIndex) => (
                                <p key={detailIndex} className="text-gray-700 leading-relaxed">
                                  {detail}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Questions About These Terms?
            </h3>
            <p className="text-gray-600 mb-4">
              If you have any questions or concerns about these Terms & Conditions, please contact us.
            </p>
            <a
              href="/contact"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;

